/*
 * Approve & send, the rep-side half (docs/2026-09-20-send-on-approve-ui-shape.md). Pure and
 * timer-injected so the page and the tests share one implementation.
 *
 * A send is two commands: `approve`, then `execute`, which queues the reply. The 10-second Undo is
 * the gap between them and lives entirely here: `execute` is issued only when the countdown
 * completes, so Undo, a closed tab or a crash before completion means nothing was queued and
 * nothing was sent. The step then simply reads "Approved, not sent". The shared outbox is untouched.
 */
import { claimsAttachment } from "../../../skills/leadcue-reply/scripts/lib/materials.ts";
import type { PlanStep } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { type DraftRevision, draftEmail, type ThreadSnapshot } from "./mail-content.ts";

export const SEND_HOLD_SECONDS = 10;

const BISON_REPLY_ID = /^bison:reply:[1-9]\d*$/;

export type SendStrip =
  | {
      kind: "ready";
      /** The mailbox the lead wrote to: the reply goes out from it. */
      from: string;
      to: string;
      /** The subject of the message being replied to, which is the thread the reply lands in. */
      subject: string;
      draftRevision: number;
      /** The files the send carries: the draft's requirement, ready, then the rep's own, by name and size. */
      files: readonly { fileName: string; bytes: number }[];
      /**
       * The draft says a file is attached and carries none. The server refuses such a send
       * (`attachment_wording_without_file`); the strip says so before the rep tries.
       */
      wordingWithoutFile: boolean;
    }
  /** The draft's files are still being read: nothing can be confirmed until they are known. */
  | { kind: "checking" }
  /** The draft says a file is attached and that file is missing or was replaced. */
  | { kind: "waiting_for_file" }
  /** The draft shares a campaign link that a rep has since removed. */
  | { kind: "setting_removed" }
  | { kind: "unresolvable" };

/**
 * The draft's file requirement as the materials route reports it (`draftMaterials`), with the files
 * the rep put on the revision (`repFiles`; an older server reports none). A server with no route
 * reports none. `"checking"` while the read has not settled.
 */
export type SendStripMaterials =
  | {
      state: "none" | "ready" | "awaiting_file" | "replaced" | "setting_removed";
      requirements: readonly { file: { fileName: string; bytes: number } | null }[];
      repFiles?: readonly { fileName: string; bytes: number }[];
    }
  | "checking";

export const NO_MATERIALS: SendStripMaterials = { state: "none", requirements: [] };

/**
 * What the send will do, read from the same records the worker reads: the thread's newest inbound
 * message is the one replied to, the approved revision is what goes out, and the files are the
 * draft's ready requirement. Anything that cannot be resolved makes the send unavailable rather than
 * guessed, including a file requirement that is not yet known or not ready.
 */
export function sendStrip(
  thread: ThreadSnapshot | null,
  drafts: readonly DraftRevision[],
  step: PlanStep,
  materials: SendStripMaterials = NO_MATERIALS,
): SendStrip {
  if (step.input.kind !== "reply") return { kind: "unresolvable" };
  const revision = step.input.draftRevision;
  const draft = drafts.find((candidate) => candidate.revision === revision);
  const trigger = thread?.messages.filter((message) => message.direction === "inbound").at(-1);
  if (!draft || !trigger || !BISON_REPLY_ID.test(trigger.id)) return { kind: "unresolvable" };
  const from = trigger.to[0]?.trim();
  const to = trigger.from.trim();
  if (!from || !to) return { kind: "unresolvable" };
  if (materials === "checking") return { kind: "checking" };
  // A file not ready blocks the send elsewhere (`material_not_ready`); it is never shown as sendable.
  if (materials.state === "setting_removed") return { kind: "setting_removed" };
  if (materials.state !== "none" && materials.state !== "ready")
    return { kind: "waiting_for_file" };
  const files = [
    ...materials.requirements.flatMap((requirement) =>
      requirement.file
        ? [{ fileName: requirement.file.fileName, bytes: requirement.file.bytes }]
        : [],
    ),
    ...(materials.repFiles ?? []).map(({ fileName, bytes }) => ({ fileName, bytes })),
  ];
  return {
    kind: "ready",
    from,
    to,
    // The thread's own subject is the fallback: an empty one is shown as nothing, never invented.
    subject: (trigger.subject.trim() || thread?.subject.trim()) ?? "",
    draftRevision: revision,
    files,
    wordingWithoutFile: files.length === 0 && claimsAttachment(draftEmail(draft).plainText),
  };
}

export type SendPhase =
  | { kind: "idle" }
  | { kind: "approving" }
  | { kind: "holding"; remaining: number }
  | { kind: "queueing" }
  | { kind: "queued" }
  /**
   * Nothing was queued. `stale`: the conversation moved on during the hold. `approve_failed`: the
   * approval was refused; `approve_unknown`: no clear answer, so it may or may not be recorded.
   */
  | { kind: "cancelled"; reason: "undo" | "stale" | "approve_failed" | "approve_unknown" }
  /** The queue command was refused, so nothing is queued. A retry reads the plan again. */
  | { kind: "queue_refused"; reason: string | null }
  /**
   * No clear answer to the queue command: it may be queued. Never retried on its own; a retry
   * sends the identical command (same operation id, same revision), which the server's command
   * ledger answers with its first result, so it cannot queue twice.
   */
  | { kind: "queue_unknown"; revision: number };

/** What one command's answer showed; `issuePlanCommand`'s outcome (`src/plan-command.ts`) is one. */
export type SendCommandOutcome =
  | { readonly kind: "applied" }
  | { readonly kind: "rejected"; readonly reason: string | null }
  | { readonly kind: "unknown" };

export type SendHoldPorts = {
  /** Records the approval. Skipped when the step is already approved and current. */
  approve: (() => Promise<SendCommandOutcome>) | null;
  /** The conversation as stored now: the revision to queue against, or null when it moved on. */
  current: () => Promise<{ revision: number } | null>;
  /** Issues the queue command under one operation id kept across every retry. */
  execute: (revision: number) => Promise<SendCommandOutcome>;
  onPhase: (phase: SendPhase) => void;
  seconds?: number;
  every?: (tick: () => void) => () => void;
};

export type SendHold = {
  press: () => Promise<void>;
  undo: () => void;
  retry: () => Promise<void>;
  dispose: () => void;
  phase: () => SendPhase;
};

const secondly = (tick: () => void): (() => void) => {
  const timer = setInterval(tick, 1000);
  return () => clearInterval(timer);
};

/** Where a refused or unclear approval leaves the send; null once it applied. */
function approvalEnd(outcome: SendCommandOutcome): SendPhase | null {
  if (outcome.kind === "applied") return null;
  return {
    kind: "cancelled",
    reason: outcome.kind === "rejected" ? "approve_failed" : "approve_unknown",
  };
}

export function createSendHold(ports: SendHoldPorts): SendHold {
  const seconds = ports.seconds ?? SEND_HOLD_SECONDS;
  const every = ports.every ?? secondly;
  let phase: SendPhase = { kind: "idle" };
  let stop: (() => void) | null = null;
  let closed = false;
  let generation = 0;
  const set = (next: SendPhase): void => {
    phase = next;
    ports.onPhase(next);
  };
  const halt = (): void => {
    stop?.();
    stop = null;
  };

  const execute = async (token: number, revision: number): Promise<void> => {
    set({ kind: "queueing" });
    let outcome: SendCommandOutcome;
    try {
      outcome = await ports.execute(revision);
    } catch {
      outcome = { kind: "unknown" };
    }
    if (closed || token !== generation) return;
    set(
      outcome.kind === "applied"
        ? { kind: "queued" }
        : outcome.kind === "rejected"
          ? { kind: "queue_refused", reason: outcome.reason }
          : { kind: "queue_unknown", revision },
    );
  };
  const queue = async (token: number): Promise<void> => {
    set({ kind: "queueing" });
    let latest: { revision: number } | null;
    try {
      latest = await ports.current();
    } catch {
      // The plan could not be read, so no queue command went out.
      if (!closed && token === generation) set({ kind: "queue_refused", reason: null });
      return;
    }
    // A hold that was undone, disposed or superseded while this read was in flight never queues.
    if (closed || token !== generation) return;
    if (latest === null) {
      set({ kind: "cancelled", reason: "stale" });
      return;
    }
    await execute(token, latest.revision);
  };

  return {
    phase: () => phase,
    async press() {
      // Inert while anything is in flight: a second tap lands on nothing.
      if (!["idle", "cancelled"].includes(phase.kind)) return;
      const token = ++generation;
      if (ports.approve) {
        set({ kind: "approving" });
        let outcome: SendCommandOutcome;
        try {
          outcome = await ports.approve();
        } catch {
          outcome = { kind: "unknown" };
        }
        if (closed || token !== generation) return;
        const ended = approvalEnd(outcome);
        if (ended) {
          set(ended);
          return;
        }
      }
      let remaining = seconds;
      set({ kind: "holding", remaining });
      stop = every(() => {
        if (closed || token !== generation || phase.kind !== "holding") return;
        remaining -= 1;
        if (remaining > 0) {
          set({ kind: "holding", remaining });
          // The page keeps listening: a newer message or an edit ends the hold before the queue.
          if (remaining % 2 === 0)
            void ports
              .current()
              .then((latest) => {
                if (latest !== null || closed || token !== generation || phase.kind !== "holding")
                  return;
                generation += 1;
                halt();
                set({ kind: "cancelled", reason: "stale" });
              })
              .catch(() => undefined);
          return;
        }
        halt();
        void queue(token);
      });
    },
    undo() {
      // Only the hold can be undone. Once the queue command is out, the send is not ours to recall.
      if (phase.kind !== "holding") return;
      generation += 1;
      halt();
      set({ kind: "cancelled", reason: "undo" });
    },
    async retry() {
      // An unclear queue is asked again as it was sent: a fresh plan read would see it queued.
      if (phase.kind === "queue_unknown") await execute(++generation, phase.revision);
      else if (phase.kind === "queue_refused") await queue(++generation);
    },
    dispose() {
      // Leaving the page mid-countdown: execute never fires, so nothing is queued or sent.
      closed = true;
      generation += 1;
      halt();
    },
  };
}
